import { http } from '@/lib/http';
import type { ManifestDocument, RoomingList } from './types';

// operator-bff supplier documents (#809):
//   GET /v1/disruptions/{urn}/hotels/{hotelRef}/rooming-list(.csv)
//   GET /v1/disruptions/{urn}/transfers/{runUrn}/manifest(.csv)
// The `.csv` twins are Excel-ready (UTF-8 BOM, `;`, Spanish headers) and carry
// the filename in `Content-Disposition`, like the reconciliation export.

const base = (urn: string) => `v1/disruptions/${encodeURIComponent(urn)}`;
const roomingPath = (urn: string, hotelRef: string) => `${base(urn)}/hotels/${encodeURIComponent(hotelRef)}/rooming-list`;
const manifestPath = (urn: string, runUrn: string) => `${base(urn)}/transfers/${encodeURIComponent(runUrn)}/manifest`;

export interface CsvFile {
  blob: Blob;
  filename: string;
}

async function fetchCsv(path: string, fallback: string): Promise<CsvFile> {
  const res = await http.get(`${path}.csv`);
  const disposition = res.headers.get('content-disposition') ?? '';
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: await res.blob(), filename: match?.[1] ?? fallback };
}

export async function fetchRoomingList(urn: string, hotelRef: string): Promise<RoomingList> {
  return http.get(roomingPath(urn, hotelRef)).json<RoomingList>();
}

export function fetchRoomingListCsv(urn: string, hotelRef: string): Promise<CsvFile> {
  return fetchCsv(roomingPath(urn, hotelRef), 'rooming-list.csv');
}

export async function fetchManifestDocument(urn: string, runUrn: string): Promise<ManifestDocument> {
  return http.get(manifestPath(urn, runUrn)).json<ManifestDocument>();
}

export function fetchManifestDocumentCsv(urn: string, runUrn: string): Promise<CsvFile> {
  return fetchCsv(manifestPath(urn, runUrn), 'manifiesto.csv');
}

/** Browser download of a fetched file — one object URL, revoked after the click. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
